/* Homepage behavior bundle (quotes carousel, lazy card video, snap-dot
   pagination). Three sections each emit this script tag, so the whole file is
   wrapped in an IIFE: class declarations stay scoped and re-execution is a
   no-op past the customElements guards. */
(() => {
  if (window.__fsHomeBundle) return;
  window.__fsHomeBundle = true;

  /* A cyclic quote strip. Original cards retain their identity while their
     display order rotates, so the selected quote always settles at the left.
     Motion and portrait progress pause while a visitor is reading or using
     the controls, and automatic paging is omitted for reduced motion. */
  class FsQuoteCarousel extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.track = this.querySelector('.fsb-quotes__track');
      this.items = [...this.querySelectorAll('.fsb-quotes__item')];
      if (!this.track || !this.items.length) return;
      this.controller = new AbortController();
      const listen = (element, type, handler, options = {}) => element?.addEventListener(type, handler, { ...options, signal: this.controller.signal });
      this.counter = this.querySelector('[data-fs-quotes-counter]');
      this.dots = this.querySelector('.fsb-dots');
      this.current = 0;
      this.elapsed = 0;
      this.interval = 4000;
      this.queue = [];
      this.inView = false;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.items.forEach((item, index) => { item.dataset.quoteIndex = String(index); });
      if (this.dots) {
        this.dots.removeAttribute('aria-hidden');
        this.dots.setAttribute('role', 'group');
        this.dots.setAttribute('aria-label', 'Choose testimonial');
        this.dots.replaceChildren(...this.items.map((item, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'fsb-quotes__dot-button';
          button.setAttribute('aria-label', `Show testimonial ${index + 1} of ${this.items.length}`);
          const glyph = document.createElement('span');
          glyph.className = 'fsb-dots__dot';
          glyph.setAttribute('aria-hidden', 'true');
          button.append(glyph);
          listen(button, 'click', () => this.goTo(index));
          listen(button, 'keydown', (event) => {
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
            event.preventDefault();
            const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.items.length - 1
              : (index + (event.key === 'ArrowRight' ? 1 : -1) + this.items.length) % this.items.length;
            this.goTo(next);
            this.dots.children[next]?.focus({ preventScroll: true });
          });
          return button;
        }));
      }
      this.querySelectorAll('[data-fs-quotes-prev], [data-fs-quotes-next]').forEach((button) => {
        button.disabled = this.items.length < 2;
      });
      listen(this.querySelector('[data-fs-quotes-prev]'), 'click', () => this.page(-1));
      listen(this.querySelector('[data-fs-quotes-next]'), 'click', () => this.page(1));
      listen(this.track, 'keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.page(event.key === 'ArrowRight' ? 1 : -1);
      });
      listen(this, 'pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        this.hovered = true;
        this.schedule();
      });
      listen(this, 'pointerleave', () => { this.hovered = false; this.schedule(); });
      const focusChanged = () => {
        const active = document.activeElement;
        this.focused = this.contains(active) && (active === this.track || active?.matches(':focus-visible'));
        this.schedule();
      };
      listen(this, 'focusin', focusChanged);
      listen(this, 'focusout', () => queueMicrotask(focusChanged));
      listen(this.track, 'pointerdown', (event) => {
        if (event.pointerType === 'mouse') return;
        this.touchStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
        this.track.setPointerCapture(event.pointerId);
        this.touching = true;
        this.schedule();
      });
      listen(this.track, 'pointerup', (event) => {
        const start = this.touchStart;
        this.touchStart = null;
        this.touching = false;
        if (start && Math.abs(event.clientX - start.x) > 35 && Math.abs(event.clientX - start.x) > Math.abs(event.clientY - start.y)) {
          this.page(event.clientX < start.x ? 1 : -1);
        } else this.schedule();
      });
      listen(this.track, 'pointercancel', () => { this.touchStart = null; this.touching = false; this.schedule(); });
      listen(document, 'visibilitychange', () => this.schedule());
      listen(this.reduced, 'change', () => {
        if (this.reduced.matches) this.animation?.finish();
        this.elapsed = 0;
        this.updateProgress();
        this.schedule();
      });
      this.observer = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting && entry.intersectionRatio >= 0.25;
        this.schedule();
      }, { threshold: 0.25 });
      this.observer.observe(this);
      this.sizeObserver = new ResizeObserver(() => this.updateHeight());
      this.items.forEach((item) => this.sizeObserver.observe(item));
      this.render();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.sizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.animation?.cancel();
    }

    schedule() {
      cancelAnimationFrame(this.frame);
      const paused = !this.inView || document.hidden || this.reduced.matches || this.hovered || this.focused || this.touching || this.items.length < 2;
      this.dataset.paused = String(Boolean(paused));
      this.lastTime = performance.now();
      if (!paused) this.frame = requestAnimationFrame((time) => this.tick(time));
    }

    tick(time) {
      this.elapsed += Math.min(time - this.lastTime, 100);
      this.lastTime = time;
      this.updateProgress();
      if (this.elapsed >= this.interval && !this.moving) this.page(1);
      else this.frame = requestAnimationFrame((next) => this.tick(next));
    }

    updateProgress() {
      this.style.setProperty('--fs-quotes-progress', String(Math.min(1, this.elapsed / this.interval)));
    }

    updateHeight() {
      this.track.style.height = matchMedia('(max-width: 749px)').matches
        ? `${this.items[this.current].getBoundingClientRect().height}px`
        : '';
    }

    render() {
      this.items.forEach((item, index) => {
        const active = index === this.current;
        item.dataset.away = String(!active);
        if (active) item.setAttribute('aria-current', 'true');
        else item.removeAttribute('aria-current');
      });
      if (this.counter) {
        this.counter.textContent = `${this.current + 1} - ${this.items.length}`;
        this.counter.setAttribute('aria-live', this.focused ? 'polite' : 'off');
      }
      if (this.dots) [...this.dots.children].forEach((button, index) => {
        const active = index === this.current;
        button.setAttribute('aria-pressed', String(active));
        button.firstElementChild.classList.toggle('is-active', active);
      });
      this.updateProgress();
      this.updateHeight();
    }

    goTo(index) {
      this.queue = [];
      if (this.moving) {
        this.requestedQuote = index;
        return;
      }
      const forward = (index - this.current + this.items.length) % this.items.length;
      if (!forward) return;
      const backward = this.items.length - forward;
      const direction = forward <= backward ? 1 : -1;
      const steps = Math.min(forward, backward);
      this.queue = Array(steps - 1).fill(direction);
      this.page(direction);
    }

    async page(direction) {
      if (this.items.length < 2) return;
      if (this.moving) {
        this.queue.push(direction);
        return;
      }
      this.moving = true;
      this.elapsed = 0;
      this.schedule();
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const distance = this.track.firstElementChild.getBoundingClientRect().width + gap;
      if (direction < 0) this.track.prepend(this.track.lastElementChild);
      this.current = (this.current + direction + this.items.length) % this.items.length;
      this.render();
      if (!this.reduced.matches) {
        this.animation = this.track.animate([
          { transform: `translateX(${direction > 0 ? 0 : -distance}px)` },
          { transform: `translateX(${direction > 0 ? -distance : 0}px)` },
        ], { duration: 1000, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'forwards' });
        try { await this.animation.finished; } catch { /* Section removal cancels motion. */ }
      }
      if (!this.isConnected) return;
      if (direction > 0) this.track.append(this.track.firstElementChild);
      this.animation?.cancel();
      this.animation = null;
      this.moving = false;
      if (this.requestedQuote != null) {
        const target = this.requestedQuote;
        this.requestedQuote = null;
        this.goTo(target);
        if (!this.moving) this.schedule();
      } else if (this.queue.length) this.page(this.queue.shift());
      else this.schedule();
    }
  }

  if (!customElements.get('fs-quote-carousel')) {
    customElements.define('fs-quote-carousel', FsQuoteCarousel);
  }

  /* The setup film loads only when it can play. The native button also gives
     keyboard users a persistent pause choice and an explicit reduced-motion play. */
  class FsLazyVideo extends HTMLElement {
    connectedCallback() {
      if (this.abort || !this.dataset.src) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.connection = navigator.connection;
      this.videoEventsBound = false;
      this.toggle = this.querySelector('.fs-lazy-video__toggle') || document.createElement('button');
      this.toggle.className = 'fs-lazy-video__toggle';
      this.toggle.type = 'button';
      this.toggle.addEventListener('click', () => {
        const playing = this.video && !this.video.paused;
        this.userPaused = playing;
        this.manualPlayback = !playing;
        this.syncPlayback();
      }, options);
      if (!this.toggle.isConnected) this.append(this.toggle);
      this.reduced.addEventListener('change', () => {
        this.manualPlayback = false;
        this.syncPlayback();
      }, options);
      this.connection?.addEventListener('change', () => this.syncPlayback(), options);
      document.addEventListener('visibilitychange', () => this.syncPlayback(), options);
      this.observer = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        this.syncPlayback();
      }, { threshold: .01 });
      this.observer.observe(this);
      this.bindVideoEvents();
      this.renderControl();
    }

    shouldPlay() {
      return this.isConnected && this.inView && !document.hidden && !this.userPaused &&
        (this.manualPlayback || (!this.reduced.matches && !this.connection?.saveData));
    }

    syncPlayback() {
      const play = this.shouldPlay();
      if (play && !this.video) {
        this.video = document.createElement('video');
        Object.assign(this.video, { muted: true, loop: true, playsInline: true, preload: 'metadata' });
        this.video.setAttribute('muted', '');
        this.video.setAttribute('playsinline', '');
        this.video.setAttribute('aria-hidden', 'true');
        this.video.src = this.dataset.src;
        this.prepend(this.video);
        this.bindVideoEvents();
      }
      if (this.video) {
        this.video.hidden = this.reduced.matches && !this.manualPlayback;
        if (play) this.video.play().then(() => {
          if (!this.shouldPlay()) this.video.pause();
          this.renderControl();
        }).catch(() => this.renderControl());
        else this.video.pause();
      }
      this.renderControl();
    }

    bindVideoEvents() {
      if (!this.video || this.videoEventsBound) return;
      this.videoEventsBound = true;
      ['play', 'pause', 'error'].forEach(type => this.video.addEventListener(type, () => this.renderControl(), { signal: this.abort.signal }));
    }

    renderControl() {
      if (!this.toggle) return;
      const playing = Boolean(this.video && !this.video.paused);
      this.toggle.setAttribute('aria-label', playing ? 'Pause setup video' : 'Play setup video');
      this.toggle.setAttribute('aria-pressed', String(playing));
      this.toggle.textContent = playing ? 'Ⅱ' : '▶';
      this.dataset.playing = String(playing);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.abort?.abort();
      this.abort = null;
      this.video?.pause();
      this.renderControl();
    }
  }

  if (!customElements.get('fs-lazy-video')) {
    customElements.define('fs-lazy-video', FsLazyVideo);
  }

  /* Bounded scroll-snap pages. Indicators are real buttons; each glyph keeps
     the reference dot/pill shape inside a separate, unambiguous touch target. */
  class FsSnapDots extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.scroller = this.querySelector('[data-fs-snap-scroller]');
      this.dotsEl = this.querySelector('.fsb-dots');
      if (!this.scroller || !this.dotsEl) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.items = [...this.scroller.children];
      this.dotsEl.removeAttribute('aria-hidden');
      this.dotsEl.setAttribute('role', 'group');
      this.dotsEl.setAttribute('aria-label', 'Choose training card');
      this.dotsEl.replaceChildren(...this.items.map((item, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'fsb-dots__button';
        button.setAttribute('aria-label', `Show training card ${index + 1} of ${this.items.length}`);
        const glyph = document.createElement('span');
        glyph.className = 'fsb-dots__dot';
        glyph.setAttribute('aria-hidden', 'true');
        button.append(glyph);
        button.addEventListener('click', () => this.goTo(index), options);
        button.addEventListener('keydown', event => this.onKey(event, index, true), options);
        return button;
      }));
      this.scroller.addEventListener('keydown', event => this.onKey(event, this.requestedIndex ?? this.current ?? 0), options);
      this.scroller.addEventListener('scroll', () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => { this.requestedIndex = null; this.update(); }, 160);
      }, { ...options, passive: true });
      ['pointerdown', 'touchstart', 'wheel'].forEach(type => this.scroller.addEventListener(type, () => { this.requestedIndex = null; }, { ...options, passive: true }));
      this.reduced.addEventListener('change', () => {
        if (this.requestedIndex != null) this.goTo(this.requestedIndex);
      }, options);
      this.sizeObserver = new ResizeObserver(() => { this.requestedIndex = null; this.update(); });
      this.sizeObserver.observe(this.scroller);
      this.items.forEach(item => this.sizeObserver.observe(item));
      this.update();
    }

    position(index) {
      const left = this.items[index].getBoundingClientRect().left - this.scroller.getBoundingClientRect().left + this.scroller.scrollLeft;
      return Math.max(0, Math.min(left, this.scroller.scrollWidth - this.scroller.clientWidth));
    }

    onKey(event, index, focusDot = false) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      event.stopPropagation();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.items.length - 1 : index + (event.key === 'ArrowRight' ? 1 : -1);
      this.goTo(next);
      if (focusDot) this.dotsEl.children[this.requestedIndex]?.focus({ preventScroll: true });
    }

    goTo(index) {
      this.requestedIndex = Math.max(0, Math.min(this.items.length - 1, index));
      this.scroller.scrollTo({ left: this.position(this.requestedIndex), behavior: this.reduced.matches ? 'instant' : 'smooth' });
      this.render(this.requestedIndex);
    }

    render(index) {
      this.current = index;
      [...this.dotsEl.children].forEach((button, item) => {
        const active = item === index;
        button.setAttribute('aria-pressed', String(active));
        button.firstElementChild.classList.toggle('is-active', active);
      });
    }

    update() {
      if (!this.items.length) return;
      let nearest = 0;
      this.items.forEach((item, index) => {
        if (Math.abs(this.position(index) - this.scroller.scrollLeft) < Math.abs(this.position(nearest) - this.scroller.scrollLeft)) nearest = index;
      });
      this.render(nearest);
      this.dotsEl.hidden = this.scroller.scrollWidth <= this.scroller.clientWidth + 4;
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      this.sizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      clearTimeout(this.scrollTimer);
    }
  }

  if (!customElements.get('fs-snap-dots')) {
    customElements.define('fs-snap-dots', FsSnapDots);
  }
})();
